import { IJourney } from '../../../models/journey.model';
import { IVehicle } from '../../../models/labs/micromobility/vehicle.model';
import { ICoordinate } from '../../../models/coordinate.model';
import { JourneyHelper } from '../../../helpers/journey.helper';
import { IStation } from '../../../models/labs/micromobility/station.model';
import { ITripLeg } from '../../../models/trip-leg.model';
import { TransportMode } from '../../../models/transport-mode.enum';
import { IMicromobilityLink, MicromobilityLinkType } from '../../../models/labs/micromobility/micromobility-link.model';
import { IDestinationLink } from '../../../models/destination-link.model';
import { DateHelper } from '../../../helpers/date.helper';
import { DateTime } from 'luxon';
import { DateTimeRelatesTo } from '../../../models/date-time-relates-to.enum';
import { StopAreaHelper } from '../../../helpers/stop-area.helper';
import { TranslationService } from '../../../services/translation.service';
import batteryLevel25Icon from '../../../../assets/icons/micromobility-battery-level-25.svg';
import batteryLevel50Icon from '../../../../assets/icons/micromobility-battery-level-50.svg';
import batteryLevel75Icon from '../../../../assets/icons/micromobility-battery-level-75.svg';
import batteryLevel100Icon from '../../../../assets/icons/micromobility-battery-level-100.svg';
import styrOchStallIcon from '../../../../assets/icons/micromobility-styrochstall-icon.svg';
import dottIcon from '../../../../assets/icons/micromobility-dott-icon.svg';
import voilIcon from '../../../../assets/icons/micromobility-voi-icon.svg';
import rydeIcon from '../../../../assets/icons/micromobility-ryde-icon.svg';
import electricScooterIcon from '../../../../assets/icons/micromobility-scooter-dark.svg';
import electricBikeIcon from '../../../../assets/icons/micromobility-bike-electric-assist-dark.svg';
import stationBikeIcon from '../../../../assets/icons/station-bike-dark.svg';
import DataUrlHelper from '../../../helpers/data-url.helper';
import { PropulsionType } from '../../../models/labs/micromobility/propulsion-type.enum';
import { FormFactor } from '../../../models/labs/micromobility/form-factor.enum';
import { IAccessLink } from '../../../models/access-link.model';
import { IMicromobilityFilters } from '../../Shared/MicromobilityFilters/micromobility-filters.model';
import { IdHelper } from '../../../helpers/id.helper';

export type VehicleWithDistance = { vehicle: IVehicle; distanceInMeters: number };
export type StationWithDistance = { station: IStation; distanceInMeters: number };

export default class MicromobilityHelper {
  public static tripLegCouldBeReplacedByMicromobility(tripLeg: ITripLeg, maxDurationInMinutes: number): boolean {
    const eligibleTransportModes = [TransportMode.bus, TransportMode.tram];
    if (!tripLeg.serviceJourney?.line || !eligibleTransportModes.includes(tripLeg.serviceJourney?.line.transportMode)) {
      return false;
    }

    if (tripLeg.plannedDurationInMinutes === undefined || tripLeg.plannedDurationInMinutes > maxDurationInMinutes) {
      return false;
    }

    return true;
  }

  public static getJourneyOrigin(journey: IJourney): ICoordinate | undefined {
    if (journey.departureAccessLink?.origin?.latitude && journey.departureAccessLink?.origin?.longitude) {
      return {
        latitude: journey.departureAccessLink.origin.latitude,
        longitude: journey.departureAccessLink.origin.longitude,
      };
    }

    const firstPublicTransportLegOriginStopArea = JourneyHelper.getFirstPublicTransportLeg(journey)?.origin.stopPoint?.stopArea;
    if (firstPublicTransportLegOriginStopArea && firstPublicTransportLegOriginStopArea.latitude && firstPublicTransportLegOriginStopArea.longitude) {
      return {
        latitude: firstPublicTransportLegOriginStopArea.latitude,
        longitude: firstPublicTransportLegOriginStopArea.longitude,
      };
    }

    return undefined;
  }

  public static getDestinationOfStartingMicromobilityLink(journey: IJourney): ICoordinate | undefined {
    if (!journey.tripLegs || journey.tripLegs.length === 0) return undefined;

    if (journey.tripLegs.length === 1) {
      return MicromobilityHelper.getJourneyDestination(journey);
    }

    const secondTripLegOriginStopArea = journey.tripLegs[1].origin.stopPoint?.stopArea;
    if (secondTripLegOriginStopArea && secondTripLegOriginStopArea.latitude && secondTripLegOriginStopArea.longitude) {
      return {
        latitude: secondTripLegOriginStopArea.latitude,
        longitude: secondTripLegOriginStopArea.longitude,
      };
    }

    return undefined;
  }

  public static getOriginOfEndingMicromobilityLink(journey: IJourney): ICoordinate | undefined {
    if (!journey.tripLegs || journey.tripLegs.length < 2) return undefined;

    const lastTripLegButOneDestinationStopArea = journey.tripLegs[journey.tripLegs.length - 2].destination.stopPoint?.stopArea;
    if (lastTripLegButOneDestinationStopArea && lastTripLegButOneDestinationStopArea.latitude && lastTripLegButOneDestinationStopArea.longitude) {
      return {
        latitude: lastTripLegButOneDestinationStopArea.latitude,
        longitude: lastTripLegButOneDestinationStopArea.longitude,
      };
    }

    return undefined;
  }

  public static calculateTimeParametersForFirstLegBikeJourneySearch(journey: IJourney, interchangeDurationInMinutes: number): { dateTime: DateTime; dateTimeRelatesTo: DateTimeRelatesTo } | undefined {
    if (!journey.tripLegs || journey.tripLegs.length === 0) return undefined;

    if (journey.tripLegs.length === 1) {
      return {
        dateTime: DateTime.fromISO(
          journey.departureAccessLink
            ? journey.departureAccessLink.origin.estimatedTime ?? journey.departureAccessLink.origin.plannedTime
            : journey.tripLegs[0].origin.estimatedTime ?? journey.tripLegs[0].origin.plannedTime
        ),
        dateTimeRelatesTo: DateTimeRelatesTo.departure,
      };
    }

    return {
      dateTime: DateTime.fromISO(journey.tripLegs[1].origin.estimatedTime ?? journey.tripLegs[1].origin.plannedTime).minus({ minutes: interchangeDurationInMinutes }),
      dateTimeRelatesTo: DateTimeRelatesTo.arrival,
    };
  }

  public static calculateTimeParametersForLastLegBikeJourneySearch(journey: IJourney, interchangeDurationInMinutes: number): { dateTime: DateTime; dateTimeRelatesTo: DateTimeRelatesTo } | undefined {
    if (!journey.tripLegs || journey.tripLegs.length < 2) return undefined;

    return {
      dateTime: DateTime.fromISO(journey.tripLegs[journey.tripLegs.length - 2].destination.estimatedTime ?? journey.tripLegs[journey.tripLegs.length - 2].destination.plannedTime).plus({
        minutes: interchangeDurationInMinutes,
      }),
      dateTimeRelatesTo: DateTimeRelatesTo.departure,
    };
  }

  public static getJourneyDestination(journey: IJourney): ICoordinate | undefined {
    if (journey.arrivalAccessLink?.destination?.latitude && journey.arrivalAccessLink?.destination?.longitude) {
      return {
        latitude: journey.arrivalAccessLink.destination.latitude,
        longitude: journey.arrivalAccessLink.destination.longitude,
      };
    }

    const lastPublicTransportLegDestinationStopArea = JourneyHelper.getLastPublicTransportLeg(journey)?.destination.stopPoint?.stopArea;
    if (lastPublicTransportLegDestinationStopArea && lastPublicTransportLegDestinationStopArea.latitude && lastPublicTransportLegDestinationStopArea.longitude) {
      return {
        latitude: lastPublicTransportLegDestinationStopArea.latitude,
        longitude: lastPublicTransportLegDestinationStopArea.longitude,
      };
    }
  }

  public static getVehiclesWithinDistance(vehicles: IVehicle[], latitude: number, longitude: number, maxDistanceInMeters: number): VehicleWithDistance[] {
    return vehicles
      .map((vehicle) => ({
        vehicle,
        distanceInMeters: calculateDistanceInMeters(latitude, longitude, vehicle.latitude, vehicle.longitude),
      }))
      .filter((vehicleWithDistance) => vehicleWithDistance.distanceInMeters <= maxDistanceInMeters)
      .sort((a, b) => a.distanceInMeters - b.distanceInMeters);
  }

  public static getStationsWithinDistance(stations: IStation[], latitude: number, longitude: number, maxDistanceInMeters: number): StationWithDistance[] {
    return stations
      .filter((station) => station.availableVehicles > 0)
      .map((station) => ({
        station: station,
        distanceInMeters: calculateDistanceInMeters(latitude, longitude, station.latitude, station.longitude),
      }))
      .filter((stationWithDistance) => stationWithDistance.distanceInMeters <= maxDistanceInMeters)
      .sort((a, b) => a.distanceInMeters - b.distanceInMeters);
  }

  public static createMicromobilityLinkForDocklessVehicle(
    vehicle: IVehicle,
    allAvailableVehicles: IVehicle[],
    bikeJourneyDestinationLink: IDestinationLink,
    fromSearchLocationName: string | undefined,
    toSearchLocationName: string | undefined
  ): IMicromobilityLink | undefined {
    if (
      !bikeJourneyDestinationLink.destination.latitude ||
      !bikeJourneyDestinationLink.destination.longitude ||
      !bikeJourneyDestinationLink.plannedDepartureTime ||
      !bikeJourneyDestinationLink.plannedArrivalTime ||
      !bikeJourneyDestinationLink.plannedDurationInMinutes ||
      !bikeJourneyDestinationLink.linkCoordinates ||
      !bikeJourneyDestinationLink.distanceInMeters
    )
      return undefined;

    return {
      id: IdHelper.newUniqueId('micromobility-link-'),
      type: MicromobilityLinkType.docklessVehicle,
      replacesLegIndex: [],
      vehicle: vehicle,
      allAvailableVehicles: allAvailableVehicles,
      destination: {
        latitude: bikeJourneyDestinationLink.destination.latitude,
        longitude: bikeJourneyDestinationLink.destination.longitude,
      },
      plannedDepartureTime: bikeJourneyDestinationLink.plannedDepartureTime,
      plannedArrivalTime: bikeJourneyDestinationLink.plannedArrivalTime,
      plannedDurationInMinutes: bikeJourneyDestinationLink.plannedDurationInMinutes,
      linkCoordinates: bikeJourneyDestinationLink.linkCoordinates || [],
      distanceInMeters: bikeJourneyDestinationLink.distanceInMeters,
      fromSearchLocationName: fromSearchLocationName,
      toSearchLocationName: toSearchLocationName,
    };
  }

  public static createMicromobilityLinkBetweenStations(
    originStation: IStation,
    destinationStation: IStation,
    bikeJourneyDestinationLink: IDestinationLink,
    fromSearchLocationName: string | undefined,
    toSearchLocationName: string | undefined
  ): IMicromobilityLink | undefined {
    if (
      !bikeJourneyDestinationLink.plannedDepartureTime ||
      !bikeJourneyDestinationLink.plannedArrivalTime ||
      !bikeJourneyDestinationLink.plannedDurationInMinutes ||
      !bikeJourneyDestinationLink.distanceInMeters
    )
      return undefined;

    return {
      id: IdHelper.newUniqueId('micromobility-link-'),
      type: MicromobilityLinkType.stationBound,
      replacesLegIndex: [],
      originStation: originStation,
      destinationStation: destinationStation,
      plannedDepartureTime: bikeJourneyDestinationLink.plannedDepartureTime,
      plannedArrivalTime: bikeJourneyDestinationLink.plannedArrivalTime,
      plannedDurationInMinutes: bikeJourneyDestinationLink.plannedDurationInMinutes,
      linkCoordinates: bikeJourneyDestinationLink.linkCoordinates || [],
      distanceInMeters: bikeJourneyDestinationLink.distanceInMeters,
      fromSearchLocationName: fromSearchLocationName,
      toSearchLocationName: toSearchLocationName,
    };
  }

  public static updateJourneyWithStartingMicromobilityLink(journey: IJourney, micromobilityLink: IMicromobilityLink, departureAccessLink: IAccessLink | undefined, legIndexToReplace: number): void {
    journey.startingMicromobilityLink = micromobilityLink;
    journey.departureAccessLink = departureAccessLink;
    micromobilityLink.replacesLegIndex.push(legIndexToReplace);
    const subsequentConnectionLink = journey.connectionLinks?.find((link) => link.journeyLegIndex === legIndexToReplace + 1);
    if (subsequentConnectionLink) micromobilityLink.replacesLegIndex.push(subsequentConnectionLink.journeyLegIndex);
    journey.tripLegs = journey.tripLegs?.filter((leg) => micromobilityLink.replacesLegIndex.indexOf(leg.journeyLegIndex) === -1);
    journey.connectionLinks = journey.connectionLinks?.filter((link) => micromobilityLink.replacesLegIndex.indexOf(link.journeyLegIndex) === -1);
    journey.allLegs = journey.allLegs?.filter((leg) => micromobilityLink.replacesLegIndex.indexOf(leg.journeyLegIndex) === -1);
    if (journey.tripLegs && journey.tripLegs.length === 0) {
      journey.arrivalAccessLink = undefined;
    }
  }

  public static updateJourneyWithEndingMicromobilityLink(journey: IJourney, micromobilityLink: IMicromobilityLink, arrivalAccessLink: IAccessLink | undefined, legIndexToReplace: number): void {
    journey.endingMicromobilityLink = micromobilityLink;
    journey.arrivalAccessLink = arrivalAccessLink;
    micromobilityLink.replacesLegIndex.push(legIndexToReplace);
    const priorConnectionLink = journey.connectionLinks?.find((link) => link.journeyLegIndex === legIndexToReplace - 1);
    if (priorConnectionLink) micromobilityLink.replacesLegIndex.push(priorConnectionLink.journeyLegIndex);
    journey.tripLegs = journey.tripLegs?.filter((leg) => micromobilityLink.replacesLegIndex.indexOf(leg.journeyLegIndex) === -1);
    journey.connectionLinks = journey.connectionLinks?.filter((link) => micromobilityLink.replacesLegIndex.indexOf(link.journeyLegIndex) === -1);
    journey.allLegs = journey.allLegs?.filter((leg) => micromobilityLink.replacesLegIndex.indexOf(leg.journeyLegIndex) === -1);
  }

  public static getJourneyStartTime(journey: IJourney): DateTime {
    const parsedRegularJourneyDepartureTime = DateHelper.parseWithFallback(JourneyHelper.getDepartureTimeForJourney(journey, true), undefined);
    const regularJourneyDepartureTime = parsedRegularJourneyDepartureTime?.isValid ? parsedRegularJourneyDepartureTime : DateTime.fromMillis(0);

    if (journey.startingMicromobilityLink) {
      const parseMicromobilityDepartureTime = DateHelper.parseWithFallback(journey.startingMicromobilityLink.plannedDepartureTime, undefined);
      if (parseMicromobilityDepartureTime?.isValid && parseMicromobilityDepartureTime < regularJourneyDepartureTime) {
        return parseMicromobilityDepartureTime;
      }
    }

    return regularJourneyDepartureTime;
  }

  public static isMicromobilityJourney(journey: IJourney): boolean {
    if (journey.startingMicromobilityLink || journey.endingMicromobilityLink) return true;
    return false;
  }

  public static getToLocationName(micromobilityLink: IMicromobilityLink): string {
    const locationName = micromobilityLink.type === MicromobilityLinkType.stationBound ? micromobilityLink.destinationStation?.name : micromobilityLink.toSearchLocationName;

    return StopAreaHelper.getNameWithoutMunicipality(locationName ?? TranslationService.get('travelplanner.micromobility.vehicleposition'));
  }

  public static createAccessLinkFromDestinationLink(destinationLink: IDestinationLink): IAccessLink | undefined {
    if (!destinationLink.plannedDepartureTime || !destinationLink.plannedArrivalTime || !destinationLink.plannedDurationInMinutes) return undefined;

    return {
      transportMode: destinationLink.transportMode,
      transportSubMode: destinationLink.transportSubMode,
      origin: {
        latitude: destinationLink.origin.latitude,
        longitude: destinationLink.origin.longitude,
        plannedTime: destinationLink.plannedDepartureTime,
        name: destinationLink.origin.name,
        platform: destinationLink.origin.platform,
        locationType: destinationLink.origin.locationType,
        notes: destinationLink.origin.notes,
      },
      destination: {
        latitude: destinationLink.destination.latitude,
        longitude: destinationLink.destination.longitude,
        plannedTime: destinationLink.plannedArrivalTime,
        name: destinationLink.destination.name,
        platform: destinationLink.destination.platform,
        locationType: destinationLink.destination.locationType,
        notes: destinationLink.destination.notes,
      },
      notes: destinationLink.notes,
      distanceInMeters: destinationLink.distanceInMeters,
      linkCoordinates: destinationLink.linkCoordinates,
    };
  }

  public static getNumberOfOtherAvailableVehicles(micromobilityLink: IMicromobilityLink): number {
    let availableVehicles: number;
    if (micromobilityLink.type === MicromobilityLinkType.stationBound) availableVehicles = micromobilityLink.originStation?.availableVehicles ?? 0;
    else availableVehicles = micromobilityLink.allAvailableVehicles?.length ?? 0;

    return Math.max(availableVehicles - 1, 0);
  }

  public static createRangeIconSrc(micromobilityVehicle: IVehicle): string | undefined {
    const currentRangePercentage = Math.floor((micromobilityVehicle.currentRangeInMeters / micromobilityVehicle.maxRangeInMeters) * 100);
    if (currentRangePercentage >= 90) return DataUrlHelper.encode(batteryLevel100Icon);
    if (currentRangePercentage >= 60) return DataUrlHelper.encode(batteryLevel75Icon);
    if (currentRangePercentage >= 25) return DataUrlHelper.encode(batteryLevel50Icon);
    return DataUrlHelper.encode(batteryLevel25Icon);
  }

  public static createRangeText(micromobilityVehicle: IVehicle): string {
    return `${Math.floor(micromobilityVehicle.currentRangeInMeters / 1000)} km ${TranslationService.get('labs.micromobility.micromobilitysearchpage.range').toLowerCase()}`;
  }

  public static getOperatorIconSrc(micromobilityLink: IMicromobilityLink): string | undefined {
    if (micromobilityLink.type === MicromobilityLinkType.stationBound) {
      return DataUrlHelper.encode(styrOchStallIcon);
    }
    if (micromobilityLink.vehicle && micromobilityLink.vehicle.operatorName === 'Voi') {
      return DataUrlHelper.encode(voilIcon);
    }
    if (micromobilityLink.vehicle && micromobilityLink.vehicle.operatorName === 'Dott') {
      return DataUrlHelper.encode(dottIcon);
    }
    if (micromobilityLink.vehicle && micromobilityLink.vehicle.operatorName === 'Ryde') {
      return DataUrlHelper.encode(rydeIcon);
    }
  }

  public static getVehicleTypeName(micromobilityLink: IMicromobilityLink): string {
    if (micromobilityLink.type === MicromobilityLinkType.stationBound) return TranslationService.get('travelplanner.micromobility.formfactor.bicycle.singular');
    if (micromobilityLink.vehicle && micromobilityLink?.vehicle.formFactor === FormFactor.Scooter) return TranslationService.get('travelplanner.micromobility.formfactor.scooter.singular');
    if (micromobilityLink.vehicle && micromobilityLink?.vehicle.formFactor === FormFactor.Bicycle && micromobilityLink?.vehicle.propulsionType === PropulsionType.ElectricAssist)
      return TranslationService.get('travelplanner.micromobility.formfactor.bicycle.electricassist.singular');
    return '';
  }

  public static getVehicleIconSrc(micromobilityLink: IMicromobilityLink): string {
    if (micromobilityLink.type === MicromobilityLinkType.stationBound) return DataUrlHelper.encode(stationBikeIcon);
    if (micromobilityLink.vehicle && micromobilityLink?.vehicle.formFactor === FormFactor.Scooter) return DataUrlHelper.encode(electricScooterIcon);
    if (micromobilityLink.vehicle && micromobilityLink?.vehicle.formFactor === FormFactor.Bicycle && micromobilityLink?.vehicle.propulsionType === PropulsionType.ElectricAssist)
      return DataUrlHelper.encode(electricBikeIcon);
    return DataUrlHelper.encode(stationBikeIcon);
  }

  public static getEndDestinationNameForMicromobilityDocklessLink(micromobilityLink: IMicromobilityLink, startJourney: boolean, firstTripLeg: ITripLeg | undefined): string | undefined {
    if (micromobilityLink && micromobilityLink.type === MicromobilityLinkType.docklessVehicle) {
      if (firstTripLeg && startJourney) return StopAreaHelper.getNameWithoutMunicipality(firstTripLeg.origin.stopPoint.name);
      return micromobilityLink.toSearchLocationName ?? undefined;
    }
    return undefined;
  }

  public static filterDocklessVehiclesByFilters(docklessVehicles: IVehicle[], microMobilityFilters: IMicromobilityFilters | undefined): IVehicle[] {
    if (!microMobilityFilters) return docklessVehicles;

    const filteredDocklessVehicles = docklessVehicles.filter((x) => {
      if (x.operatorName === 'Voi' && x.formFactor === FormFactor.Scooter && !microMobilityFilters.showScootersVoi) return false;
      if (x.operatorName === 'Ryde' && x.formFactor === FormFactor.Scooter && !microMobilityFilters.showScootersRyde) return false;
      if (x.operatorName === 'Voi' && x.formFactor === FormFactor.Bicycle && !microMobilityFilters.showElectricBikesVoi) return false;
      return true;
    });

    return filteredDocklessVehicles;
  }
}

function calculateDistanceInMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000; // Earth's radius in meters
  const toRadians = (degrees: number) => degrees * (Math.PI / 180);

  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}
